import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  FolderOpen, Upload, Trash2, FileText, Image, FileSpreadsheet,
  File, Loader2, FolderPlus, X, Tag, Plus, Search
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FILE_ICONS = {
  pdf: FileText, docx: FileText, doc: FileText,
  xlsx: FileSpreadsheet, xls: FileSpreadsheet, csv: FileSpreadsheet,
  png: Image, jpg: Image, jpeg: Image, webp: Image, gif: Image, bmp: Image,
};

function getFileIcon(filename) {
  const ext = filename.split(".").pop().toLowerCase();
  return FILE_ICONS[ext] || File;
}

function formatFileSize(bytes) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

const SUGGESTED_TAGS = [
  "lease", "permits", "financials", "menu", "design", "vendor",
  "insurance", "marketing", "operations", "training", "legal", "floor-plan",
  "equipment", "branding", "recipes", "hr", "contracts",
];

export default function StoreDocumentsTab({ storeId, storeName }) {
  const [documents, setDocuments] = useState([]);
  const [allTags, setAllTags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [filterTag, setFilterTag] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [uploadTags, setUploadTags] = useState([]);
  const [tagInput, setTagInput] = useState("");
  const [dragActive, setDragActive] = useState(false);
  const [editingTagsFor, setEditingTagsFor] = useState(null);
  const [editTags, setEditTags] = useState([]);
  const [editTagInput, setEditTagInput] = useState("");

  useEffect(() => {
    fetchTags();
    fetchDocuments();
  }, [storeId, filterTag]);

  const fetchTags = async () => {
    try { const res = await axios.get(`${API}/documents/tags/all`); setAllTags(res.data); } catch (e) { console.error(e); }
  };

  const fetchDocuments = async () => {
    try {
      const params = { store_id: storeId };
      if (filterTag) params.tag = filterTag;
      const res = await axios.get(`${API}/documents`, { params });
      setDocuments(res.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const uploadFiles = async (files) => {
    if (!files || files.length === 0) return;
    setUploading(true);
    let successCount = 0;
    for (const file of files) {
      try {
        const formData = new FormData();
        formData.append("file", file);
        formData.append("store_id", storeId);
        if (uploadTags.length > 0) formData.append("tags", uploadTags.join(","));
        await axios.post(`${API}/documents/upload`, formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        successCount++;
      } catch (e) { toast.error(`Failed to upload ${file.name}`); }
    }
    if (successCount > 0) {
      toast.success(`${successCount} file${successCount > 1 ? "s" : ""} uploaded`);
      fetchDocuments();
      fetchTags();
    }
    setUploading(false);
  };

  const deleteDocument = async (id) => {
    try {
      await axios.delete(`${API}/documents/${id}`);
      toast.success("Document deleted");
      fetchDocuments();
      fetchTags();
    } catch (e) { toast.error("Failed to delete document"); }
  };

  const addUploadTag = (tag) => {
    const t = tag.trim().toLowerCase();
    if (t && !uploadTags.includes(t)) setUploadTags([...uploadTags, t]);
    setTagInput("");
  };

  const removeUploadTag = (tag) => {
    setUploadTags(uploadTags.filter((t) => t !== tag));
  };

  const saveDocTags = async (docId) => {
    try {
      await axios.put(`${API}/documents/${docId}/tags`, { tags: editTags });
      toast.success("Tags updated");
      setEditingTagsFor(null);
      fetchDocuments();
      fetchTags();
    } catch (e) { toast.error("Failed to update tags"); }
  };

  const addEditTag = (tag) => {
    const t = tag.trim().toLowerCase();
    if (t && !editTags.includes(t)) setEditTags([...editTags, t]);
    setEditTagInput("");
  };

  const handleDrag = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") setDragActive(true);
    else if (e.type === "dragleave") setDragActive(false);
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files?.length > 0) uploadFiles(e.dataTransfer.files);
  }, [storeId, uploadTags]);

  const handleFileInput = (e) => {
    if (e.target.files?.length > 0) { uploadFiles(e.target.files); e.target.value = ""; }
  };

  const filteredDocs = documents.filter((d) => {
    if (!searchQuery) return true;
    return d.original_name.toLowerCase().includes(searchQuery.toLowerCase());
  });

  const tagSuggestions = SUGGESTED_TAGS.filter(
    (t) => !uploadTags.includes(t) && t.includes(tagInput.toLowerCase())
  );
  const editTagSuggestions = SUGGESTED_TAGS.filter(
    (t) => !editTags.includes(t) && t.includes(editTagInput.toLowerCase())
  );

  const allAvailableTags = [...new Set([...allTags, ...SUGGESTED_TAGS])].sort();

  return (
    <div data-testid="store-documents-tab">
      {/* Filters */}
      <div className="flex items-center gap-3 mb-6 flex-wrap">
        <div className="relative flex-1 max-w-xs">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
          <Input
            data-testid="store-doc-search"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search documents..."
            className="pl-9 text-sm"
          />
        </div>
        <Select value={filterTag || "all"} onValueChange={(v) => setFilterTag(v === "all" ? "" : v)}>
          <SelectTrigger data-testid="store-filter-tag-select" className="w-44 text-sm">
            <SelectValue placeholder="Filter by tag" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Tags</SelectItem>
            {allAvailableTags.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
          </SelectContent>
        </Select>
        {filterTag && (
          <button
            onClick={() => setFilterTag("")}
            className="flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium bg-amber-100 text-amber-800 rounded-full hover:bg-amber-200 transition-colors"
          >
            <Tag className="w-3 h-3" /> {filterTag} <X className="w-3 h-3" />
          </button>
        )}
      </div>

      {/* Upload Area */}
      <div className="mb-8 bg-white border border-stone-200/60 rounded-xl p-5 shadow-sm">
        {/* Tag Input for Upload */}
        <div className="mb-4">
          <p className="text-xs font-medium text-stone-600 mb-2">Tags for new uploads</p>
          <div className="flex flex-wrap gap-1.5 mb-2">
            {uploadTags.map((tag) => (
              <span key={tag} className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium bg-stone-100 text-stone-700 rounded-full">
                {tag}
                <button onClick={() => removeUploadTag(tag)} className="hover:text-red-600"><X className="w-3 h-3" /></button>
              </span>
            ))}
          </div>
          <div className="flex gap-2">
            <Input
              data-testid="store-upload-tag-input"
              value={tagInput}
              onChange={(e) => setTagInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") { e.preventDefault(); addUploadTag(tagInput); }
              }}
              placeholder="Add tag (e.g., lease, permits)..."
              className="text-sm flex-1 max-w-xs"
            />
            <Button
              data-testid="store-add-upload-tag-btn"
              variant="outline" size="sm"
              onClick={() => addUploadTag(tagInput)}
              disabled={!tagInput.trim()}
              className="text-xs"
            >
              <Plus className="w-3 h-3 mr-1" /> Add
            </Button>
          </div>
          {tagInput && tagSuggestions.length > 0 && (
            <div className="flex flex-wrap gap-1 mt-2">
              {tagSuggestions.slice(0, 8).map((t) => (
                <button
                  key={t}
                  onClick={() => addUploadTag(t)}
                  className="px-2 py-0.5 text-xs bg-stone-50 border border-stone-200 text-stone-600 rounded-full hover:bg-stone-100 transition-colors"
                >
                  + {t}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Drop zone */}
        <div
          data-testid="store-upload-dropzone"
          onDragEnter={handleDrag} onDragLeave={handleDrag}
          onDragOver={handleDrag} onDrop={handleDrop}
          className={`relative border-2 border-dashed rounded-xl p-8 text-center transition-all duration-200 cursor-pointer
            ${dragActive ? "border-amber-500 bg-amber-50/50" : "border-stone-300 hover:border-stone-400 hover:bg-stone-50/50"}
            ${uploading ? "pointer-events-none opacity-60" : ""}`}
          onClick={() => document.getElementById("store-file-upload-input")?.click()}
        >
          <input id="store-file-upload-input" data-testid="store-file-upload-input" type="file" multiple onChange={handleFileInput} className="hidden"
            accept=".pdf,.docx,.doc,.xlsx,.xls,.csv,.txt,.md,.json,.xml,.png,.jpg,.jpeg,.webp,.gif,.bmp,.tiff" />
          {uploading ? (
            <div className="flex flex-col items-center gap-2">
              <Loader2 className="w-8 h-8 text-stone-400 animate-spin" />
              <p className="text-sm text-stone-500">Uploading...</p>
            </div>
          ) : (
            <div className="flex flex-col items-center gap-2">
              <Upload className={`w-8 h-8 ${dragActive ? "text-amber-500" : "text-stone-400"}`} />
              <p className="text-sm font-medium text-stone-700">{dragActive ? "Drop files here" : "Drag & drop files or click to browse"}</p>
              <p className="text-xs text-stone-400">PDF, Word, Excel, CSV, images, text files</p>
            </div>
          )}
        </div>
      </div>

      {/* Documents List */}
      {filteredDocs.length === 0 && !loading ? (
        <div className="text-center py-16 bg-stone-50 rounded-xl border-2 border-dashed border-stone-200">
          <FolderPlus className="w-10 h-10 text-stone-300 mx-auto mb-3" />
          <h3 className="text-lg font-medium text-stone-700 mb-2" style={{ fontFamily: "'Playfair Display', serif" }}>
            {documents.length > 0 ? "No matching documents" : "No documents for this store"}
          </h3>
          <p className="text-sm text-stone-500 max-w-md mx-auto">
            {documents.length > 0
              ? "Try adjusting your search or filters."
              : "Upload lease agreements, permits, floor plans, or any other store documents."}
          </p>
        </div>
      ) : (
        <div className="space-y-2">
          {filteredDocs.map((doc) => {
            const IconComponent = getFileIcon(doc.original_name);
            const isEditingTags = editingTagsFor === doc.id;
            return (
              <div
                key={doc.id}
                data-testid={`store-document-item-${doc.id}`}
                className="flex items-start gap-3 px-4 py-3 bg-white border border-stone-200/60 rounded-xl shadow-sm hover:shadow-md transition-shadow"
              >
                <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5 ${
                  doc.file_type === "image" ? "bg-violet-50" : "bg-stone-100"
                }`}>
                  <IconComponent className={`w-4 h-4 ${doc.file_type === "image" ? "text-violet-600" : "text-stone-600"}`} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-stone-900 truncate">{doc.original_name}</p>
                  <p className="text-xs text-stone-400 mb-1.5">
                    {formatFileSize(doc.file_size)} · {doc.created_at?.slice(0, 10)}
                  </p>
                  {/* Tags display */}
                  {isEditingTags ? (
                    <div className="mt-1">
                      <div className="flex flex-wrap gap-1 mb-1.5">
                        {editTags.map((tag) => (
                          <span key={tag} className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium bg-amber-100 text-amber-800 rounded-full">
                            {tag}
                            <button onClick={() => setEditTags(editTags.filter(t => t !== tag))} className="hover:text-red-600"><X className="w-2.5 h-2.5" /></button>
                          </span>
                        ))}
                      </div>
                      <div className="flex gap-1.5 items-center">
                        <Input
                          data-testid={`store-edit-tag-input-${doc.id}`}
                          value={editTagInput}
                          onChange={(e) => setEditTagInput(e.target.value)}
                          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addEditTag(editTagInput); } }}
                          placeholder="Add tag..."
                          className="text-xs h-7 max-w-[140px]"
                        />
                        {editTagInput && editTagSuggestions.length > 0 && (
                          <div className="flex gap-1 flex-wrap">
                            {editTagSuggestions.slice(0, 4).map((t) => (
                              <button key={t} onClick={() => addEditTag(t)} className="px-1.5 py-0.5 text-xs bg-stone-50 border border-stone-200 rounded-full hover:bg-stone-100">+ {t}</button>
                            ))}
                          </div>
                        )}
                        <Button data-testid={`store-save-tags-${doc.id}`} size="sm" className="h-7 text-xs bg-stone-900 text-stone-50 hover:bg-stone-800 px-3" onClick={() => saveDocTags(doc.id)}>Save</Button>
                        <Button variant="ghost" size="sm" className="h-7 text-xs px-2" onClick={() => setEditingTagsFor(null)}>Cancel</Button>
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-center gap-1 flex-wrap">
                      {(doc.tags || []).map((tag) => (
                        <span key={tag} className="inline-flex items-center px-2 py-0.5 text-xs font-medium bg-stone-100 text-stone-600 rounded-full">{tag}</span>
                      ))}
                      <button
                        data-testid={`store-edit-tags-btn-${doc.id}`}
                        onClick={() => { setEditingTagsFor(doc.id); setEditTags(doc.tags || []); setEditTagInput(""); }}
                        className="inline-flex items-center gap-0.5 px-2 py-0.5 text-xs text-stone-400 hover:text-stone-600 hover:bg-stone-50 rounded-full transition-colors"
                      >
                        <Tag className="w-3 h-3" />
                        {(doc.tags || []).length === 0 ? "Add tags" : "Edit"}
                      </button>
                    </div>
                  )}
                </div>
                <Button
                  data-testid={`store-delete-doc-${doc.id}`}
                  variant="ghost" size="sm"
                  onClick={() => deleteDocument(doc.id)}
                  className="text-stone-400 hover:text-red-600 hover:bg-red-50 rounded-lg px-2 flex-shrink-0"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </Button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
